// Typed client for SnipCell.Api. Handles JWT storage, the Authorization header, and
// transparent refresh on 401. Endpoints mirror the backend contract documented for
// /api/auth/*, /api/usage/*, /api/error-reports.
import { config } from './config'
import type { AuthResponse, ClientInfo, Provider, ValidateResponse } from '../types/auth'

const ACCESS_KEY = 'snipcell.accessToken'
const REFRESH_KEY = 'snipcell.refreshToken'
const CLIENT_ID_KEY = 'snipcell.clientId'

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY)
}
function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY)
}
export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access)
  localStorage.setItem(REFRESH_KEY, refresh)
}
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

/** A stable per-install id (no machine fingerprinting is possible in a browser). */
export function clientInfo(): ClientInfo {
  let id = localStorage.getItem(CLIENT_ID_KEY)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(CLIENT_ID_KEY, id)
  }
  return {
    platform: 'Web',
    appVersion: config.appVersion,
    machineId: id,
    timestamp: new Date().toISOString(),
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  auth?: boolean // attach Bearer token (default true)
  retried?: boolean // internal: prevents refresh loops
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (opts.auth !== false) {
    const token = getAccessToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }

  let res: Response
  try {
    res = await fetch(config.apiBaseUrl + path, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    })
  } catch (err) {
    // Network/CORS failures surface here (e.g. CORS not yet enabled on the API).
    throw new ApiError(0, `Network error reaching the API (${(err as Error).message}). ` +
      `If this persists, CORS may not be enabled for this origin yet.`)
  }

  if (res.status === 401 && opts.auth !== false && !opts.retried) {
    const refreshed = await tryRefresh()
    if (refreshed) return request<T>(path, { ...opts, retried: true })
  }

  if (!res.ok) {
    throw new ApiError(res.status, await safeMessage(res))
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

async function safeMessage(res: Response): Promise<string> {
  try {
    const data = await res.json()
    return (data?.message || data?.error || data?.title || JSON.stringify(data)) as string
  } catch {
    return `${res.status} ${res.statusText}`
  }
}

let refreshInFlight: Promise<boolean> | null = null
function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  const refreshToken = getRefreshToken()
  if (!refreshToken) return Promise.resolve(false)
  refreshInFlight = (async () => {
    try {
      const res = await fetch(config.apiBaseUrl + '/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken, clientInfo: clientInfo() }),
      })
      if (!res.ok) return false
      const data = (await res.json()) as AuthResponse
      setTokens(data.accessToken, data.refreshToken)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

// ---- Auth endpoints ----

export function authenticateEmail(email: string, password: string): Promise<AuthResponse> {
  return request<AuthResponse>('/api/auth/email', {
    method: 'POST',
    auth: false,
    body: { email, password, clientInfo: clientInfo() },
  })
}

export function authenticateProvider(providerToken: string, provider: Provider): Promise<AuthResponse> {
  return request<AuthResponse>('/api/auth/authenticate', {
    method: 'POST',
    auth: false,
    body: { providerToken, provider, clientInfo: clientInfo() },
  })
}

export function validateSession(): Promise<ValidateResponse> {
  return request<ValidateResponse>('/api/auth/validate', { method: 'GET' })
}

export function signOut(): Promise<void> {
  return request<void>('/api/auth/signout', { method: 'POST' })
}

// ---- Telemetry ----

/** Usage sessions require an authenticated user (JWT). */
export function postUsage(payload: Record<string, unknown>): Promise<void> {
  return request<void>('/api/usage/sessions', { method: 'POST', body: payload })
}

/** Error reports are anonymous (best-effort, never throws to callers). */
export async function reportError(payload: Record<string, unknown>): Promise<void> {
  try {
    await request<void>('/api/error-reports', { method: 'POST', auth: false, body: payload })
  } catch {
    /* swallow */
  }
}

// ---- Payments (Stripe via the backend) ----
// NOTE: the backend authenticates these with a Firebase ID token. Until that token is
// available in the add-in, the Upgrade button routes users to the web billing portal
// (see lib/license.ts → openUpgrade). These are here for future in-app checkout.

export function createCheckoutSession(
  planKey: string,
  billingPeriod: string,
  currency: string,
): Promise<{ checkoutUrl: string }> {
  return request('/api/payments/create-checkout-session', {
    method: 'POST',
    body: { planKey, billingPeriod, currency },
  })
}

export function createPortalSession(): Promise<{ portalUrl: string }> {
  return request('/api/payments/create-portal-session', { method: 'POST' })
}
